import { useAuth } from "@/context/auth.context";

/**
 * Hard-coded super-admin emails that always have full access.
 * This mirrors the server-side SUPER_ADMIN_EMAILS constant.
 * Must be kept in sync with server/src/config/super-admin.ts
 */
const SUPER_ADMIN_EMAILS = ["admin@admin.com"];

function isSuperAdminEmail(email?: string | null): boolean {
  if (!email) return false;
  const lower = email.toLowerCase().trim();
  return SUPER_ADMIN_EMAILS.some((e) => e.toLowerCase() === lower);
}

/**
 * Returns true if the user is a cooperative leader (President, Secretary, Accountant)
 * or a system admin — matching the isAdmin() logic in utils.ts.
 * These roles get full admin-level permissions including portal switching.
 */
function hasAdminPrivileges(user: any): boolean {
  if (!user) return false;
  if (user.isAdmin === true) return true;
  const leadershipRole = user.leadershipRole || user.role?.name || user.role;
  const adminRoles = ["Technical Support", "President", "Secretary", "Accountant"];
  if (adminRoles.includes(leadershipRole)) return true;
  if (user.isGroupLeader === true) {
    const validLeadershipRoles = ["President", "Secretary", "Accountant", "Admin", "Technical Support"];
    return validLeadershipRoles.includes(leadershipRole);
  }
  return false;
}

export function can(user: any, permission: string): boolean {
  if (!user) return false;

  // Hard-coded super-admin emails always have full access
  if (isSuperAdminEmail(user?.email)) return true;

  // Admins (system admins + cooperative leaders) bypass all permission checks
  if (hasAdminPrivileges(user)) {
    if (user.permissions && permission in user.permissions) {
      return user.permissions[permission] !== false;
    }
    return true;
  }
  return user.permissions?.[permission] === true;
}

export function usePermissions() {
  const { user } = useAuth();

  return {
    can: (permission: string) => can(user, permission),
    canAny: (...permissions: string[]) => permissions.some(p => can(user, p)),
    canAll: (...permissions: string[]) => permissions.every(p => can(user, p)),
  };
}
